import { linkedSignal, Service, signal } from '@angular/core';

export type FloorPlanViewportTransform = [number, number, number, number, number, number];

/** Outlives the floor plan viewer so returning to the doors page restores the previous view instead of resetting it. */
@Service()
export class FloorPlanViewStateService {
  private readonly viewportTransforms = new Map<string, FloorPlanViewportTransform>();
  private readonly images = new Map<string, string>();
  private scopedServiceId: string | undefined;

  /** Set by the doors page. A service belongs to one project, so this alone scopes the cached view state. */
  readonly serviceId = signal<string>('');

  readonly selectedFloorPlanId = linkedSignal<string, string | undefined>({
    source: () => this.serviceId(),
    computation: () => undefined,
  });

  readonly activeWorkspaceTab = linkedSignal<string, string>({
    source: () => this.serviceId(),
    computation: () => 'list',
  });

  getViewportTransform(floorPlanId: string): FloorPlanViewportTransform | undefined {
    this.scopeToCurrentService();
    return this.viewportTransforms.get(floorPlanId);
  }

  setViewportTransform(floorPlanId: string, transform: FloorPlanViewportTransform): void {
    this.scopeToCurrentService();
    this.viewportTransforms.set(floorPlanId, [...transform]);
  }

  getImage(floorPlanId: string): string | undefined {
    this.scopeToCurrentService();
    return this.images.get(floorPlanId);
  }

  setImage(floorPlanId: string, image: string): void {
    this.scopeToCurrentService();
    this.images.set(floorPlanId, image);
  }

  clearImages(): void {
    this.images.clear();
  }

  private scopeToCurrentService(): void {
    const serviceId = this.serviceId();
    if (serviceId === this.scopedServiceId) return;

    this.scopedServiceId = serviceId;
    this.viewportTransforms.clear();
    this.images.clear();
  }
}
